import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import PageLayout from "../components/layout/PageLayout";
import PageHero from "../components/shared/PageHero";
import SectionHeading from "../components/shared/SectionHeading";
import SectionDivider from "../components/decor/SectionDivider";
import Mandala from "../components/decor/Mandala";
import { useLang } from "../i18n/LanguageContext";

const BHOOTHARAJA_IMG =
  "https://customer-assets-jt897jd0.emergentagent.net/job_vadiraja-legacy/artifacts/fcq2ctsr_SriBhootharajaru.jpeg";

const COPY = {
  heroEye: { en: "About Sri Bhootharaja", kn: "ಶ್ರೀ ಭೂತರಾಜರ ಬಗ್ಗೆ" },
  heroTitle: { en: "Sri Bhootharajaru", kn: "ಶ್ರೀ ಭೂತರಾಜರು" },
  heroSubtitle: {
    en: "The Divine Guardian of Sri Sode Kshetra",
    kn: "ಶ್ರೀ ಸೋದೆ ಕ್ಷೇತ್ರದ ದಿವ್ಯ ಕ್ಷೇತ್ರಪಾಲಕ",
  },
  intro: {
    en: "Sri Bhootharajaru occupies a unique and revered place in the spiritual traditions of Sri Sode Kshetra. Regarded as the eternal guardian of the sacred abode of Bhavi Sameera Sri Vadiraja Teertharu, he symbolizes unwavering devotion, divine strength, and selfless service. Every devotee visiting Sode traditionally seeks the blessings of Sri Bhootharajaru before proceeding to the Brindavana of Sri Vadiraja and the temples within the Matha. This sacred tradition continues to be observed by devotees from across the world.",
    kn: "ಶ್ರೀ ಸೋದೆ ಕ್ಷೇತ್ರದ ಆಧ್ಯಾತ್ಮಿಕ ಪರಂಪರೆಯಲ್ಲಿ ಶ್ರೀ ಭೂತರಾಜರು ಅನನ್ಯ ಹಾಗೂ ಪೂಜ್ಯ ಸ್ಥಾನವನ್ನು ಹೊಂದಿದ್ದಾರೆ. ಭಾವಿ ಸಮೀರ ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರ ಪವಿತ್ರ ಸನ್ನಿಧಿಯ ಶಾಶ್ವತ ಕ್ಷೇತ್ರಪಾಲಕರೆಂದು ಪೂಜಿಸಲ್ಪಡುವ ಇವರು, ಅಚಲ ಭಕ್ತಿ, ದಿವ್ಯ ಶಕ್ತಿ ಹಾಗೂ ನಿಸ್ವಾರ್ಥ ಸೇವೆಯ ಪ್ರತೀಕ. ಸೋದೆಗೆ ಭೇಟಿ ನೀಡುವ ಪ್ರತಿಯೊಬ್ಬ ಭಕ್ತರೂ ಶ್ರೀ ವಾದಿರಾಜರ ಬೃಂದಾವನ ಹಾಗೂ ಮಠದೊಳಗಿನ ದೇವಾಲಯಗಳಿಗೆ ತೆರಳುವ ಮುನ್ನ ಶ್ರೀ ಭೂತರಾಜರ ಆಶೀರ್ವಾದವನ್ನು ಪಡೆಯುವುದು ಸಂಪ್ರದಾಯ. ಈ ಪವಿತ್ರ ಪರಂಪರೆಯನ್ನು ಪ್ರಪಂಚದಾದ್ಯಂತದ ಭಕ್ತರು ಇಂದಿಗೂ ಆಚರಿಸುತ್ತಿದ್ದಾರೆ.",
  },
  // Section 1
  s1Eye: { en: "The Divine Guardian", kn: "ದಿವ್ಯ ಕ್ಷೇತ್ರಪಾಲಕ" },
  s1Title: { en: "Who is Sri Bhootharajaru?", kn: "ಶ್ರೀ ಭೂತರಾಜರು ಯಾರು?" },
  s1P1: {
    en: "Sri Bhootharajaru is revered as the Kshetrapalaka, or divine guardian, of Sri Sode Kshetra. He is regarded in the tradition as an amsha of Patrataapa Rudra, whose spiritual journey culminated in becoming the eternal servant of Bhavi Sameera Sri Vadiraja Teertharu. Rather than being worshipped as an independent deity, Sri Bhootharajaru is honoured for his absolute devotion to his Guru and his role as the protector of the sacred kshetra and its devotees.",
    kn: "ಶ್ರೀ ಸೋದೆ ಕ್ಷೇತ್ರದ ಕ್ಷೇತ್ರಪಾಲಕರೆಂದೂ, ದಿವ್ಯ ರಕ್ಷಕರೆಂದೂ ಶ್ರೀ ಭೂತರಾಜರು ಪೂಜ್ಯರಾಗಿದ್ದಾರೆ. ಪರಂಪರೆಯ ಪ್ರಕಾರ ಇವರು ಪತ್ರತಾಪ ರುದ್ರರ ಅಂಶವಾಗಿದ್ದು, ಆಧ್ಯಾತ್ಮಿಕ ಪ್ರಯಾಣದ ಕೊನೆಯಲ್ಲಿ ಭಾವಿ ಸಮೀರ ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರ ಶಾಶ್ವತ ಸೇವಕರಾದರು. ಸ್ವತಂತ್ರ ದೇವತೆಯಾಗಿ ಪೂಜಿಸಲ್ಪಡುವ ಬದಲಾಗಿ, ಗುರುಗಳ ಮೇಲಿನ ಸಂಪೂರ್ಣ ಭಕ್ತಿ ಮತ್ತು ಪವಿತ್ರ ಕ್ಷೇತ್ರ ಹಾಗೂ ಭಕ್ತರ ರಕ್ಷಕ ಪಾತ್ರಕ್ಕಾಗಿ ಇವರು ಪೂಜ್ಯರಾಗಿದ್ದಾರೆ.",
  },
  s1P2: {
    en: "His life, as narrated in the traditional accounts, is a profound reminder that sincere repentance, divine grace, and unwavering Guru bhakti can transform even the gravest of destinies into eternal service.",
    kn: "ಸಾಂಪ್ರದಾಯಿಕ ಕಥಾನಕಗಳಲ್ಲಿ ವಿವರಿಸಲಾದ ಇವರ ಜೀವನವು, ಪ್ರಾಮಾಣಿಕ ಪಶ್ಚಾತ್ತಾಪ, ದಿವ್ಯ ಕೃಪೆ ಹಾಗೂ ಅಚಲ ಗುರುಭಕ್ತಿಯು ಅತ್ಯಂತ ಕಠಿಣ ಪ್ರಾರಬ್ಧವನ್ನೂ ಶಾಶ್ವತ ಸೇವೆಯಾಗಿ ಪರಿವರ್ತಿಸಬಲ್ಲದು ಎಂಬ ಗಂಭೀರ ಸಂದೇಶವನ್ನು ನೀಡುತ್ತದೆ.",
  },

  // Section 2 - Journey
  s2Eye: { en: "Four Sacred Lives", kn: "ನಾಲ್ಕು ಪವಿತ್ರ ಜನ್ಮಗಳು" },
  s2Title: { en: "The Divine Journey", kn: "ದಿವ್ಯ ಪ್ರಯಾಣ" },
  journey: [
    {
      no: "01",
      name: { en: "Tapodhana", kn: "ತಪೋಧನ" },
      era: { en: "Treta Yuga", kn: "ತ್ರೇತಾಯುಗ" },
      text: {
        en: "During Treta Yuga, Patrataapa Rudra is said to have incarnated as the Brahmin Tapodhana. Following an incident in the court of Lord Sri Rama, Tapodhana was destined to undergo births that would ultimately lead to redemption through service to the one who would become Vayu in the next Kalpa.",
        kn: "ತ್ರೇತಾಯುಗದಲ್ಲಿ ಪತ್ರತಾಪ ರುದ್ರರು ಬ್ರಾಹ್ಮಣ ತಪೋಧನನಾಗಿ ಅವತರಿಸಿದರೆಂದು ಹೇಳಲಾಗುತ್ತದೆ. ಶ್ರೀ ರಾಮನ ಸಭೆಯಲ್ಲಿ ನಡೆದ ಘಟನೆಯಿಂದಾಗಿ ತಪೋಧನನಿಗೆ ಮುಂದಿನ ಕಲ್ಪದಲ್ಲಿ ವಾಯುವಾಗಲಿರುವವರ ಸೇವೆಯಿಂದ ಅಂತಿಮ ಮುಕ್ತಿಯನ್ನು ಪಡೆಯುವ ಜನ್ಮಗಳ ಪರಂಪರೆಯನ್ನು ಹೊಂದುವ ಪ್ರಾರಬ್ಧವು ಒದಗಿತು.",
      },
    },
    {
      no: "02",
      name: { en: "Somadatta", kn: "ಸೋಮದತ್ತ" },
      era: { en: "Mahabharata Era", kn: "ಮಹಾಭಾರತ ಕಾಲ" },
      text: {
        en: "In the Mahabharata era, the same divine being is described as taking birth as Somadatta, renowned for his courage, wisdom, and devotion. His life formed another stage in the spiritual progression toward final liberation.",
        kn: "ಮಹಾಭಾರತ ಕಾಲದಲ್ಲಿ ಇದೇ ದಿವ್ಯ ಚೇತನವು ಸೋಮದತ್ತನಾಗಿ ಜನ್ಮ ತಳೆದನೆಂದು ಹೇಳಲಾಗುತ್ತದೆ. ಧೈರ್ಯ, ಜ್ಞಾನ ಮತ್ತು ಭಕ್ತಿಗಾಗಿ ಪ್ರಸಿದ್ಧನಾದ ಇವನ ಜೀವನವು ಅಂತಿಮ ಮೋಕ್ಷಕ್ಕಾಗಿ ಸಾಗುತ್ತಿದ್ದ ಆಧ್ಯಾತ್ಮಿಕ ಪ್ರಯಾಣದ ಮತ್ತೊಂದು ಹಂತವಾಯಿತು.",
      },
    },
    {
      no: "03",
      name: { en: "Narayana Sharma", kn: "ನಾರಾಯಣ ಶರ್ಮ" },
      era: { en: "Age of Sri Vadiraja", kn: "ಶ್ರೀ ವಾದಿರಾಜರ ಕಾಲ" },
      text: {
        en: "Later, he was born as Narayana Sharma, a learned scholar and devoted disciple of Sri Vadiraja Teertharu. After witnessing a yogic miracle that his Guru had instructed disciples not to observe, he incurred a curse that transformed him into a Brahmarakshasa.",
        kn: "ನಂತರ ಇವರು ನಾರಾಯಣ ಶರ್ಮನಾಗಿ ಜನಿಸಿದರು. ಇವರು ವಿದ್ವಾಂಸ ಹಾಗೂ ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರ ಭಕ್ತ ಶಿಷ್ಯ. ಗುರುಗಳು ಶಿಷ್ಯರಿಗೆ ನೋಡಬಾರದೆಂದು ಸೂಚಿಸಿದ್ದ ಯೋಗಿಕ ಚಮತ್ಕಾರವನ್ನು ವೀಕ್ಷಿಸಿದ ಕಾರಣ, ಇವರಿಗೆ ಶಾಪವಾಗಿ ಬ್ರಹ್ಮರಾಕ್ಷಸನಾಗುವ ಪ್ರಾಪ್ತಿ ದೊರಕಿತು.",
      },
    },
    {
      no: "04",
      name: { en: "Sri Bhootharajaru", kn: "ಶ್ರೀ ಭೂತರಾಜರು" },
      era: { en: "Eternal Guardian", kn: "ಶಾಶ್ವತ ಕ್ಷೇತ್ರಪಾಲಕ" },
      text: {
        en: "Through the boundless compassion of Sri Vadiraja Teertharu, Narayana Sharma was liberated from the curse, regained his divine form, and was blessed to remain forever in the service of his Guru. From that time, he became known as Sri Bhootharajaru, the eternal guardian of Sri Sode Kshetra.",
        kn: "ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರ ಅಪಾರ ಕರುಣೆಯಿಂದ ನಾರಾಯಣ ಶರ್ಮನು ಶಾಪದಿಂದ ಮುಕ್ತನಾಗಿ, ತನ್ನ ದಿವ್ಯ ಸ್ವರೂಪವನ್ನು ಮತ್ತೆ ಪಡೆದು, ಗುರುಗಳ ಸೇವೆಯಲ್ಲಿ ಶಾಶ್ವತವಾಗಿ ಇರುವ ಆಶೀರ್ವಾದವನ್ನು ಪಡೆದನು. ಆ ಸಮಯದಿಂದ ಇವರು ಶ್ರೀ ಸೋದೆ ಕ್ಷೇತ್ರದ ಶಾಶ್ವತ ಕ್ಷೇತ್ರಪಾಲಕರಾದ ಶ್ರೀ ಭೂತರಾಜರೆಂದು ಪ್ರಸಿದ್ಧರಾದರು.",
      },
    },
  ],

  // Section 3
  s3Eye: { en: "Guru Kripa", kn: "ಗುರುಕೃಪೆ" },
  s3Title: { en: "The Grace of the Guru", kn: "ಗುರುಗಳ ಕೃಪೆ" },
  s3P1: {
    en: "One of the most cherished episodes in the Sode tradition describes how Sri Vadiraja Teertharu personally redeemed his disciple from the state of Brahmarakshasa. After answering the question posed by Narayana Sharma in the forest, the Guru released him from the curse. The disciple, restored to his divine nature, sought only one blessing, to serve his Guru forever.",
    kn: "ಸೋದೆ ಪರಂಪರೆಯ ಅತ್ಯಂತ ಪೂಜ್ಯ ಘಟನೆಗಳಲ್ಲಿ ಒಂದು, ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರು ಸ್ವತಃ ತಮ್ಮ ಶಿಷ್ಯನನ್ನು ಬ್ರಹ್ಮರಾಕ್ಷಸ ಸ್ಥಿತಿಯಿಂದ ಬಿಡುಗಡೆಗೊಳಿಸಿದ ಪ್ರಸಂಗ. ಅರಣ್ಯದಲ್ಲಿ ನಾರಾಯಣ ಶರ್ಮನು ಕೇಳಿದ ಪ್ರಶ್ನೆಗೆ ಉತ್ತರ ನೀಡಿದ ಬಳಿಕ, ಗುರುಗಳು ಶಾಪದಿಂದ ಆತನನ್ನು ಮುಕ್ತಗೊಳಿಸಿದರು. ದಿವ್ಯ ಸ್ವರೂಪವನ್ನು ಮತ್ತೆ ಪಡೆದ ಶಿಷ್ಯನು ತನ್ನ ಗುರುಗಳಿಗೆ ಶಾಶ್ವತವಾಗಿ ಸೇವೆ ಸಲ್ಲಿಸುವ ಒಂದೇ ಒಂದು ಆಶೀರ್ವಾದವನ್ನು ಬೇಡಿದನು.",
  },
  s3P2: {
    en: "Moved by this devotion, Sri Vadiraja blessed him to remain eternally at Sode as its guardian, protecting the sacred kshetra and all who come with faith.",
    kn: "ಈ ಭಕ್ತಿಯಿಂದ ಸ್ಪಂದಿತರಾದ ಶ್ರೀ ವಾದಿರಾಜರು ಸೋದೆಯ ಶಾಶ್ವತ ಕ್ಷೇತ್ರಪಾಲಕರಾಗಿ ಉಳಿಯುವಂತೆ ಆತನಿಗೆ ಆಶೀರ್ವಾದ ನೀಡಿ, ಪವಿತ್ರ ಕ್ಷೇತ್ರ ಮತ್ತು ಶ್ರದ್ಧೆಯಿಂದ ಬರುವ ಎಲ್ಲರನ್ನೂ ರಕ್ಷಿಸುವ ಜವಾಬ್ದಾರಿಯನ್ನು ವಹಿಸಿಕೊಟ್ಟರು.",
  },

  // Section 4
  s4Eye: { en: "Sacred Mission", kn: "ದಿವ್ಯ ಕರ್ತವ್ಯ" },
  s4Title: {
    en: "Bringing Sri Trivikrama to Sode",
    kn: "ಶ್ರೀ ತ್ರಿವಿಕ್ರಮನನ್ನು ಸೋದೆಗೆ ತಂದ ಕಥೆ",
  },
  s4P: {
    en: "A celebrated tradition of the Matha recounts that Sri Vadiraja Teertharu instructed Sri Bhootharajaru to bring the Utsava Murti of Lord Sri Trivikrama from Badarikashrama within an auspicious muhurta. During this divine mission, he encountered and overcame a demon who attempted to obstruct the journey. The idol was successfully brought to Sode, where Sri Vadiraja consecrated it. The distinctive three wheeled chariot associated with the Trivikrama Temple is remembered as a symbol of this sacred episode.",
    kn: "ಮಠದ ಪ್ರಸಿದ್ಧ ಪರಂಪರೆಯ ಪ್ರಕಾರ, ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರು ಶ್ರೀ ಭೂತರಾಜರಿಗೆ ಬದರಿಕಾಶ್ರಮದಿಂದ ಶ್ರೀ ತ್ರಿವಿಕ್ರಮನ ಉತ್ಸವ ಮೂರ್ತಿಯನ್ನು ಶುಭ ಮುಹೂರ್ತದೊಳಗೆ ತರುವಂತೆ ಆದೇಶಿಸಿದರು. ಈ ದಿವ್ಯ ಕರ್ತವ್ಯದ ವೇಳೆ ಪ್ರಯಾಣಕ್ಕೆ ಅಡ್ಡಿಪಡಿಸಲು ಪ್ರಯತ್ನಿಸಿದ ರಾಕ್ಷಸನನ್ನು ಇವರು ಎದುರಿಸಿ ಸೋಲಿಸಿದರು. ಮೂರ್ತಿಯನ್ನು ಸೋದೆಗೆ ಯಶಸ್ವಿಯಾಗಿ ತಂದು, ಶ್ರೀ ವಾದಿರಾಜರು ಪ್ರತಿಷ್ಠಾಪಿಸಿದರು. ತ್ರಿವಿಕ್ರಮ ದೇವಸ್ಥಾನದ ವಿಶಿಷ್ಟವಾದ ಮೂರು ಚಕ್ರಗಳ ರಥವು ಈ ಪವಿತ್ರ ಪ್ರಸಂಗದ ಪ್ರತೀಕವಾಗಿ ಸ್ಮರಿಸಲ್ಪಡುತ್ತದೆ.",
  },

  // Section 5
  s5Eye: { en: "Worship at Sode", kn: "ಸೋದೆಯಲ್ಲಿ ಪೂಜೆ" },
  s5Title: { en: "Worship at Sri Sode Kshetra", kn: "ಶ್ರೀ ಸೋದೆ ಕ್ಷೇತ್ರದಲ್ಲಿ ಪೂಜೆ" },
  s5P1: {
    en: "Visitors to Sri Sode Kshetra traditionally offer prayers at the shrine of Sri Bhootharajaru before proceeding to the Brindavana of Sri Vadiraja Teertharu and the main temples. The shrine is situated near the sacred Dhavala Ganga within the Matha premises.",
    kn: "ಶ್ರೀ ಸೋದೆ ಕ್ಷೇತ್ರಕ್ಕೆ ಬರುವ ಭಕ್ತರು ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರ ಬೃಂದಾವನ ಹಾಗೂ ಮುಖ್ಯ ದೇವಾಲಯಗಳಿಗೆ ತೆರಳುವ ಮುನ್ನ ಶ್ರೀ ಭೂತರಾಜರ ಸನ್ನಿಧಿಯಲ್ಲಿ ಪ್ರಾರ್ಥನೆ ಸಲ್ಲಿಸುವ ಸಂಪ್ರದಾಯವನ್ನು ಪಾಲಿಸುತ್ತಾರೆ. ಸನ್ನಿಧಿಯು ಮಠದ ಆವರಣದೊಳಗಿನ ಪವಿತ್ರ ಧವಳ ಗಂಗೆಯ ಬಳಿ ಇದೆ.",
  },
  s5P2: {
    en: "A distinctive tradition observed by devotees is the offering and rolling of coconuts as an expression of faith and surrender. Many devotees also offer coconuts symbolically for each wheel of their vehicle before undertaking journeys, praying for protection and divine blessings. A daily evening Bhoota Bali ritual is also performed at the shrine.",
    kn: "ಭಕ್ತರು ಪಾಲಿಸುವ ವಿಶಿಷ್ಟ ಸಂಪ್ರದಾಯವೆಂದರೆ, ಶ್ರದ್ಧೆ ಹಾಗೂ ಶರಣಾಗತಿಯ ಪ್ರತೀಕವಾಗಿ ತೆಂಗಿನಕಾಯಿಗಳನ್ನು ಸಮರ್ಪಿಸಿ ಉರುಳಿಸುವುದು. ಪ್ರಯಾಣ ಆರಂಭಿಸುವ ಮುನ್ನ ಅನೇಕ ಭಕ್ತರು ತಮ್ಮ ವಾಹನದ ಪ್ರತಿ ಚಕ್ರಕ್ಕೂ ತೆಂಗಿನಕಾಯಿಯನ್ನು ಸಾಂಕೇತಿಕವಾಗಿ ಸಮರ್ಪಿಸಿ ರಕ್ಷಣೆ ಹಾಗೂ ದಿವ್ಯ ಆಶೀರ್ವಾದಕ್ಕಾಗಿ ಪ್ರಾರ್ಥಿಸುತ್ತಾರೆ. ಪ್ರತಿನಿತ್ಯ ಸಂಜೆ ಸನ್ನಿಧಿಯಲ್ಲಿ ಭೂತಬಲಿ ಆಚರಣೆಯನ್ನೂ ನಡೆಸಲಾಗುತ್ತದೆ.",
  },

  // Section 6
  s6Eye: { en: "Eternal Lessons", kn: "ಶಾಶ್ವತ ಬೋಧನೆಗಳು" },
  s6Title: { en: "The Message of Sri Bhootharajaru", kn: "ಶ್ರೀ ಭೂತರಾಜರ ಸಂದೇಶ" },
  lessons: [
    {
      en: "True greatness lies in humble service to the Guru.",
      kn: "ನಿಜವಾದ ಶ್ರೇಷ್ಠತೆ ಗುರುಗಳ ವಿನಮ್ರ ಸೇವೆಯಲ್ಲಿಯೇ ಅಡಗಿದೆ.",
    },
    {
      en: "Repentance transforms destiny through divine grace.",
      kn: "ಪಶ್ಚಾತ್ತಾಪವು ದಿವ್ಯ ಕೃಪೆಯ ಮೂಲಕ ಪ್ರಾರಬ್ಧವನ್ನೇ ಪರಿವರ್ತಿಸುತ್ತದೆ.",
    },
    {
      en: "Guru bhakti is the path to liberation.",
      kn: "ಗುರುಭಕ್ತಿಯೇ ಮೋಕ್ಷಕ್ಕೆ ಮಾರ್ಗ.",
    },
    {
      en: "Dharma is protected by unwavering devotion.",
      kn: "ಅಚಲ ಭಕ್ತಿಯಿಂದ ಧರ್ಮವು ರಕ್ಷಿಸಲ್ಪಡುತ್ತದೆ.",
    },
    {
      en: "Divine protection accompanies sincere faith.",
      kn: "ಪ್ರಾಮಾಣಿಕ ಶ್ರದ್ಧೆಯೊಂದಿಗೆ ದಿವ್ಯ ರಕ್ಷಣೆ ಸದಾ ಇರುತ್ತದೆ.",
    },
  ],

  // Closing
  closingQuote: {
    en: "May the blessings of Sri Bhootharajaru protect every devotee, remove obstacles from the path of righteousness, and inspire unwavering devotion to Sri Hari through the grace of Bhavi Sameera Sri Vadiraja Teertharu.",
    kn: "ಶ್ರೀ ಭೂತರಾಜರ ಆಶೀರ್ವಾದವು ಪ್ರತಿಯೊಬ್ಬ ಭಕ್ತನನ್ನೂ ರಕ್ಷಿಸಲಿ, ಧರ್ಮ ಮಾರ್ಗದ ಎಲ್ಲ ಅಡ್ಡಿಗಳನ್ನೂ ದೂರ ಮಾಡಲಿ, ಹಾಗೂ ಭಾವಿ ಸಮೀರ ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥರ ಕೃಪೆಯ ಮೂಲಕ ಶ್ರೀ ಹರಿಯ ಮೇಲಿನ ಅಚಲ ಭಕ್ತಿಯನ್ನು ಪ್ರೇರೇಪಿಸಲಿ.",
  },
  ctaBtn: { en: "Plan Your Visit to Sri Sode Kshetra", kn: "ಶ್ರೀ ಸೋದೆ ಕ್ಷೇತ್ರಕ್ಕೆ ನಿಮ್ಮ ಭೇಟಿ ಯೋಜಿಸಿ" },
};

const Bhootharaja = () => {
  const { lang } = useLang();
  const L = (o) => (o && o[lang]) || (o && o.en) || "";

  return (
    <PageLayout>
      <PageHero
        eyebrow={L(COPY.heroEye)}
        title={L(COPY.heroTitle)}
        subtitle={L(COPY.heroSubtitle)}
      />

      {/* Hero image + intro - portrait photo LHS, intro RHS */}
      <section className="pt-16 pb-10" data-testid="bhootharaja-intro">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="relative lg:col-span-5">
            <div className="aspect-[3/4] rounded-3xl overflow-hidden shadow-2xl border-4 border-[#D4AF37]/40 ring-1 ring-[#B65A14]/20 bg-[#F2E7D5]">
              <img
                src={BHOOTHARAJA_IMG}
                alt="Sri Bhootharajaru, Divine Guardian of Sri Sode Kshetra"
                className="w-full h-full object-contain"
                data-testid="bhootharaja-hero-image"
              />
            </div>
            <div className="absolute -top-8 -left-8 opacity-60 pointer-events-none">
              <Mandala size={130} color="#D4AF37" opacity={0.7} />
            </div>
            <div className="absolute -bottom-8 -right-8 opacity-60 pointer-events-none">
              <Mandala size={130} color="#B65A14" opacity={0.5} />
            </div>
          </div>
          <div className="lg:col-span-7 lg:pt-4">
            <div className="overline mb-3 text-[#B65A14]">{L(COPY.heroEye)}</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#3B2A1A] leading-tight mb-5">
              {L(COPY.heroTitle)}
            </h2>
            <div className="gold-underline mb-6" />
            <p className="text-lg leading-relaxed text-[#3B2A1A]/90">
              {L(COPY.intro)}
            </p>
          </div>
        </div>
      </section>

      <SectionDivider variant="lotus" />

      {/* Section 1 - Who is Sri Bhootharajaru */}
      <section className="py-16" data-testid="bhootharaja-who">
        <div className="max-w-4xl mx-auto px-4 lg:px-8">
          <SectionHeading
            eyebrow={L(COPY.s1Eye)}
            title={L(COPY.s1Title)}
            align="center"
          />
          <div className="mt-10 space-y-5 text-[#3B2A1A]/85 leading-relaxed text-[17px]">
            <p>{L(COPY.s1P1)}</p>
            <p className="italic text-[#B65A14]/90 border-l-2 border-[#D4AF37] pl-5">{L(COPY.s1P2)}</p>
          </div>
        </div>
      </section>

      {/* Section 2 - Divine Journey */}
      <section className="bg-sand-texture py-16 relative overflow-hidden" data-testid="bhootharaja-journey">
        <div className="absolute -top-24 -right-24 opacity-30 animate-mandala-slow">
          <Mandala size={340} color="#B65A14" opacity={0.5} />
        </div>
        <div className="max-w-6xl mx-auto px-4 lg:px-8 relative">
          <SectionHeading
            eyebrow={L(COPY.s2Eye)}
            title={L(COPY.s2Title)}
            align="center"
          />
          <div className="mt-14 grid md:grid-cols-2 gap-6">
            {COPY.journey.map((j, i) => (
              <div
                key={i}
                className="relative bg-[#FBF7F0] border border-[#D4AF37]/30 rounded-2xl p-8 card-lift overflow-hidden"
                data-testid={`bhootharaja-journey-${i + 1}`}
              >
                <div className="absolute -top-6 -right-6 opacity-15">
                  <Mandala size={110} color="#D4AF37" opacity={0.9} />
                </div>
                <div className="flex items-start gap-5 relative">
                  <div className="shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-[#B65A14] to-[#3B2A1A] text-[#D4AF37] flex items-center justify-center font-serif-display text-xl">
                    {j.no}
                  </div>
                  <div className="flex-1">
                    <div className="overline text-[#B65A14] mb-1">{L(j.era)}</div>
                    <h3 className="font-serif-display text-2xl text-[#3B2A1A] mb-3">
                      {L(j.name)}
                    </h3>
                    <p className="text-[15px] text-[#3B2A1A]/80 leading-relaxed">
                      {L(j.text)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SectionDivider variant="lotus" />

      {/* Section 3 - Grace of the Guru */}
      <section className="py-16" data-testid="bhootharaja-grace">
        <div className="max-w-4xl mx-auto px-4 lg:px-8">
          <SectionHeading
            eyebrow={L(COPY.s3Eye)}
            title={L(COPY.s3Title)}
            align="center"
          />
          <div className="mt-10 space-y-5 text-[#3B2A1A]/85 leading-relaxed text-[17px]">
            <p>{L(COPY.s3P1)}</p>
            <p>{L(COPY.s3P2)}</p>
          </div>
        </div>
      </section>

      {/* Section 4 - Trivikrama */}
      <section className="py-16 bg-[#F2E7D5]/40 border-y border-[#D4AF37]/25 relative overflow-hidden" data-testid="bhootharaja-trivikrama">
        <div className="absolute -bottom-24 -left-24 opacity-25 animate-mandala-slow">
          <Mandala size={320} color="#D4AF37" opacity={0.6} />
        </div>
        <div className="max-w-4xl mx-auto px-4 lg:px-8 relative">
          <SectionHeading
            eyebrow={L(COPY.s4Eye)}
            title={L(COPY.s4Title)}
            align="center"
          />
          <div className="mt-10 text-[#3B2A1A]/85 leading-relaxed text-[17px]">
            <p>{L(COPY.s4P)}</p>
          </div>
        </div>
      </section>

      {/* Section 5 - Worship */}
      <section className="py-16" data-testid="bhootharaja-worship">
        <div className="max-w-4xl mx-auto px-4 lg:px-8">
          <SectionHeading
            eyebrow={L(COPY.s5Eye)}
            title={L(COPY.s5Title)}
            align="center"
          />
          <div className="mt-10 space-y-5 text-[#3B2A1A]/85 leading-relaxed text-[17px]">
            <p>{L(COPY.s5P1)}</p>
            <p>{L(COPY.s5P2)}</p>
          </div>
        </div>
      </section>

      <SectionDivider variant="lotus" />

      {/* Section 6 - Eternal Lessons */}
      <section className="py-16" data-testid="bhootharaja-lessons">
        <div className="max-w-4xl mx-auto px-4 lg:px-8">
          <SectionHeading
            eyebrow={L(COPY.s6Eye)}
            title={L(COPY.s6Title)}
            align="center"
          />
          <div className="mt-10 relative bg-[#FBF7F0] border-2 border-[#D4AF37]/40 rounded-3xl p-8 sm:p-12 shadow-inner overflow-hidden">
            <div className="absolute -top-8 -right-8 opacity-20">
              <Mandala size={160} color="#B65A14" opacity={0.7} />
            </div>
            <div className="absolute -bottom-8 -left-8 opacity-20">
              <Mandala size={160} color="#D4AF37" opacity={0.7} />
            </div>
            <ul className="relative space-y-4">
              {COPY.lessons.map((lesson, i) => (
                <li
                  key={i}
                  className="flex items-start gap-4"
                  data-testid={`bhootharaja-lesson-${i + 1}`}
                >
                  <span className="shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B65A14] text-[#FBF7F0] flex items-center justify-center">
                    <Sparkles className="w-4 h-4" />
                  </span>
                  <p className="text-[17px] text-[#3B2A1A]/90 leading-relaxed pt-1">
                    {L(lesson)}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Closing Section */}
      <section className="py-16 relative overflow-hidden bg-gradient-to-b from-transparent to-[#F2E7D5]/60" data-testid="bhootharaja-closing">
        <div className="absolute inset-x-0 top-0 flex justify-center opacity-40 pointer-events-none">
          <Mandala size={220} color="#D4AF37" opacity={0.6} />
        </div>
        <div className="max-w-3xl mx-auto px-4 lg:px-8 relative text-center">
          <div className="text-[#B65A14] font-serif-display text-5xl leading-none mb-6">“</div>
          <blockquote className="font-serif-display text-2xl sm:text-3xl text-[#3B2A1A] leading-snug italic">
            {L(COPY.closingQuote)}
          </blockquote>
          <div className="mt-4 gold-underline mx-auto" />
          <div className="mt-10">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full bg-[#B65A14] text-[#FBF7F0] px-8 py-4 text-sm font-semibold hover:bg-[#8F4610] transition shadow-lg"
              data-testid="bhootharaja-cta-visit"
            >
              {L(COPY.ctaBtn)} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default Bhootharaja;
